import { type Page_UsernameInviteMutation } from '#app/__generated__/relay/Page_UsernameInviteMutation.graphql.js';
import {
  CONTRA_SOCIAL_REFERRAL_PROGRAM,
  REFERRAL_ACTION_PORTFOLIOS_WAITLIST,
  REFERRAL_ACTION_USER_REFERRAL,
  REFERRAL_C4C_DEMO,
  REFERRAL_C4C_SIGN_UP,
} from '#app/constants/referral.js';
import { AppLoader } from '#app/features/app/components/AppLoader.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { usePersistedReferralIntent } from '#app/hooks/usePersistedReferralIntent.js';
import { useRouteParameter } from '#app/hooks/useRouteParameter.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { logger } from '#app/services/logger.js';
import { appendSearch } from '#app/utilities/appendSearch.js';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { type StringifiableRecord } from '@contra/utilities/queryString';
import { Helmet } from 'react-helmet-async';

const log = logger.child({ namespace: 'InviteUsernameRouteMutation' });

const determineRedirectTo = ({
  action,
  inviterUsername,
  referralIntentId,
  userType = 'independent',
}: {
  action: null | string;
  inviterUsername?: string;
  referralIntentId: null | string;
  userType?: 'client' | 'independent';
}): {
  additionalParameters?: StringifiableRecord;
  redirectTo?: string;
  shouldExpedite?: true;
} => {
  switch (action) {
    case CONTRA_SOCIAL_REFERRAL_PROGRAM:
      return {
        additionalParameters: {
          inviterUsername: inviterUsername ?? undefined,
          isReferral: 'true',
          onboardingSource: 'social-referral',
          referralIntentId: referralIntentId ?? undefined,
          skipUserTypeSelection: 'true',
          userType: 'independent',
        },
      };
    case REFERRAL_ACTION_PORTFOLIOS_WAITLIST:
      return {
        redirectTo: getRoute('/portfolio', {
          search: {
            referralIntentId: referralIntentId ?? undefined,
            upgradeNow: true,
          },
        }),
      };
    case REFERRAL_ACTION_USER_REFERRAL:
      return {
        additionalParameters: {
          isReferral: 'true',
          skipUserTypeSelection: 'true',
          userType,
        },
      };
    case REFERRAL_C4C_DEMO:
      return {
        additionalParameters: {
          isReferral: 'true',
        },
      };
    case REFERRAL_C4C_SIGN_UP:
      return {
        additionalParameters: {
          skipUserTypeSelection: 'true',
          userType,
        },
      };
    default:
      return { redirectTo: undefined };
  }
};

export const Page = () => {
  const inviteUsername = useRouteParameter('username');
  const navigation = useNavigation();
  const [urlSearchParameters] = useURLParameters('/invite/@username');
  const action = urlSearchParameters.action ?? null;
  const referralExperimentNid = urlSearchParameters.referralExperimentNid;
  const referralIntentId = urlSearchParameters.referralIntentId;
  const userType = urlSearchParameters.userType;
  const { referralIntent, setReferralIntent } = usePersistedReferralIntent();
  const [createReferralIntent] = useContraMutation<Page_UsernameInviteMutation>(
    graphql`
      mutation Page_UsernameInviteMutation($input: CreateReferralIntentInput!) {
        createReferralIntent(input: $input) {
          errors {
            __typename
            message
          }

          referralIntentId
          referrerUserProfile {
            displayUsername
            firstName
            lastName
          }
        }
      }
    `,
  );

  const redirectUser = (createdReferralIntentId: null | string) => {
    const {
      additionalParameters = {},
      redirectTo,
      shouldExpedite,
    } = determineRedirectTo({
      action,
      inviterUsername: inviteUsername ?? undefined,
      referralIntentId: createdReferralIntentId,
      userType,
    });
    const signupRoute = getRoute('/sign-up', {
      search: {
        expedited: shouldExpedite,
        redirectTo,
      },
    });
    const requestADemoRoute = getRoute('/request-a-demo');

    if (action === REFERRAL_C4C_DEMO) {
      navigation.replace(appendSearch(requestADemoRoute, additionalParameters));
    } else {
      navigation.replace(appendSearch(signupRoute, additionalParameters));
    }
  };

  useMount(() => {
    if (referralIntent) {
      redirectUser(referralIntent.id);
      return;
    }

    createReferralIntent({
      onCompleted: (errors, response) => {
        const { apiErrors, payloadErrors } = errors;

        if (payloadErrors) {
          for (const payloadError of payloadErrors) {
            const errorContext = {
              locations: payloadError.locations,
              severity: payloadError.severity,
            };
            captureException(payloadError.message, {
              extra: errorContext,
            });
            log.error(errorContext, payloadError.message);
          }

          return;
        }

        if (apiErrors) {
          for (const apiError of apiErrors) {
            log.debug(apiError, apiError.message);
          }
        }

        if (response.createReferralIntent.referralIntentId) {
          setReferralIntent({
            id: response.createReferralIntent.referralIntentId,
            intentAction: action,
            referrer: response.createReferralIntent?.referrerUserProfile
              ? {
                  displayUsername:
                    response.createReferralIntent.referrerUserProfile
                      .displayUsername,
                  firstName:
                    response.createReferralIntent.referrerUserProfile
                      .firstName ?? '',
                  lastName:
                    response.createReferralIntent.referrerUserProfile
                      .lastName ?? '',
                }
              : null,
          });
        }

        redirectUser(response.createReferralIntent.referralIntentId);
      },
      onError: (error) => {
        captureException(error);
        redirectUser(null);
      },
      variables: {
        input: {
          referralExperimentNid,
          referralIntentId,
          referrerUsername: inviteUsername,
        },
      },
    });
  });

  return (
    <>
      <Helmet>
        <title>Redirecting...</title>
      </Helmet>
      <AppLoader />
    </>
  );
};
